import axios from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Me {
  id: string; name: string; email: string; role: string; roleKey: string;
  caps: Record<string, boolean>; dealerId: string | null;
  tenant: { code: string; name: string; currency: string; timezone: string; logo_url: string | null; brand_color?: string | null; kind?: 'live' | 'demo' | 'sandbox' };
  /** Netxol support session opened from Netxol Admin (who, read-only or not). */
  support?: { by: string; name: string; ro: boolean } | null;
  /** one.netxol.com: the Netxol AI budget is set by Netxol, not by the ISP. */
  aiBudgetByNetxol?: boolean;
}

interface AuthState {
  /** Access token — in memory only; the refresh token lives in an HttpOnly cookie Core sets (never readable here). */
  accessToken: string | null;
  expiresAt: number | null;
  user: Me | null;
  /** The session has been restored (or found missing) after the page loaded. */
  ready: boolean;
  setSession: (s: { accessToken: string; expiresAt: number; user: Me }) => void;
  setUser: (u: Me) => void;
  clear: () => void;
}

// Core, Netxol Admin and the portal keep long-lived tokens in HttpOnly cookies for requests that say they come from
// this page (lib/session-cookie.ts on the server)
axios.defaults.headers.common['X-Netxol-Client'] = 'web';
/** A support session from Netxol Admin has no refresh token; it is kept for this tab only, until it expires. */
const SUPPORT_KEY = 'nx.support';

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null, expiresAt: null, user: null, ready: false,
      setSession: (s) => set({ accessToken: s.accessToken, expiresAt: s.expiresAt, user: s.user, ready: true }),
      setUser: (u) => set({ user: u }),
      clear: () => { try { sessionStorage.removeItem(SUPPORT_KEY); } catch { /* private mode */ } set({ accessToken: null, expiresAt: null, user: null, ready: true }); },
    }),
    // only the profile is remembered (language, currency); never a token
    { name: 'netxol.session', partialize: (s) => ({ user: s.user }) as Partial<AuthState> },
  ),
);

/** A support session (token from Netxol Admin, 30 minutes, no refresh). */
export function startSupportSession(token: string, expiresAt: number, user: Me) {
  try { sessionStorage.setItem(SUPPORT_KEY, JSON.stringify({ token, expiresAt })); } catch { /* private mode */ }
  useAuth.getState().setSession({ accessToken: token, expiresAt, user });
}

/** After the page loads: the support session of this tab, else a new access token from the refresh cookie. */
export async function restoreSession() {
  const st = useAuth.getState();
  try {
    const sup = JSON.parse(sessionStorage.getItem(SUPPORT_KEY) ?? 'null') as { token: string; expiresAt: number } | null;
    if (sup && sup.expiresAt * 1000 > Date.now() + 5_000 && st.user) { st.setSession({ accessToken: sup.token, expiresAt: sup.expiresAt, user: st.user }); return; }
  } catch { /* no session storage */ }
  try {
    const { data } = await axios.post('/api/core/auth/refresh', {});
    st.setSession(data);
  } catch { st.clear(); }
}

export type Cap = string;
/** True if the user holds any of the caps (super implies everything). */
export function can(user: Me | null, ...caps: Cap[]) {
  if (!user) return false;
  if (user.caps.super) return true;
  if (!caps.length) return true;
  return caps.some((c) => user.caps[c]);
}
export const useCan = (...caps: Cap[]) => can(useAuth((s) => s.user), ...caps);
